import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Spinner } from '@/components/ui';
import { LogIn, Mail } from 'lucide-react';
import { StagingBanner } from '@/components/layout/StagingBanner';

// Where to go after sign-in: the page that sent us here, including its query and
// #fragment (the user manual's /manual?next=…#section hop depends on both).
function returnPath(state: unknown): string {
  const from = (state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  return from?.pathname ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}` : '/';
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    loginWithGoogle,
    sendMagicLink,
    completeMagicLink,
    isMagicLinkUrl,
    currentUser,
    loading: authLoading,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Magic link state
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [magicSentTo, setMagicSentTo] = useState<string | null>(null);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [completingMagicLink, setCompletingMagicLink] = useState(false);
  const magicLinkHandledRef = useRef(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && currentUser) {
      const from = returnPath(location.state);
      navigate(from, { replace: true });
    }
  }, [currentUser, authLoading, navigate, location]);

  // If we were kicked out by AuthContext's onAuthStateChanged catch-all, show
  // the real reason on the login page instead of letting the user guess.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('auth:lastError');
      if (!raw) return;
      sessionStorage.removeItem('auth:lastError');
      const payload = JSON.parse(raw) as { message?: string; code?: string | null };
      const msg = payload.message || 'Something went wrong loading your account.';
      setError(`You were signed out: ${msg}${payload.code ? ` (${payload.code})` : ''}`);
    } catch { /* non-fatal */ }
  }, []);

  // Complete a magic-link sign-in when the page is opened from the emailed
  // link. Guarded by a ref — the oobCode is single-use, so StrictMode's double
  // effect invocation in dev must not redeem it twice.
  useEffect(() => {
    if (!isMagicLinkUrl() || magicLinkHandledRef.current) return;
    magicLinkHandledRef.current = true;
    setCompletingMagicLink(true);
    completeMagicLink()
      .catch((err: any) => {
        if (err?.code === 'auth/missing-email') {
          // Link opened on a different device/browser — confirm the email to finish.
          setMode('magic');
          setNeedsEmailConfirm(true);
        } else {
          console.error('Magic link sign-in error:', err);
          setError(getErrorMessage(err));
        }
      })
      .finally(() => setCompletingMagicLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      const from = returnPath(location.state);
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      const from = returnPath(location.state);
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Google login error:', err);
      // CRITICAL: Show error message for invite-only restriction
      if (err.message && err.message.includes('invite-only')) {
        setError('Signup is invite-only. Please contact your organization administrator for an invitation.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (needsEmailConfirm) {
        // Redeem the link in the URL with the confirmed email; on success the
        // currentUser redirect effect navigates away.
        await completeMagicLink(email);
        setNeedsEmailConfirm(false);
      } else {
        await sendMagicLink(email);
        setMagicSentTo(email);
      }
    } catch (err: any) {
      console.error('Magic link error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: 'password' | 'magic') => {
    setMode(next);
    setError('');
    setMagicSentTo(null);
  };

  const getErrorMessage = (error: any): string => {
    const code = error.code || '';
    if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      return 'Invalid email or password';
    }
    if (code === 'auth/invalid-email') {
      return 'Invalid email address';
    }
    if (code === 'auth/user-disabled') {
      return 'This account has been disabled';
    }
    if (code === 'auth/too-many-requests') {
      return 'Too many failed attempts. Please try again later';
    }
    if (code === 'auth/popup-closed-by-user') {
      return 'Sign-in popup was closed';
    }
    if (code === 'auth/cancelled-popup-request') {
      return 'Only one popup can be opened at a time';
    }
    if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
      return 'This sign-in link is invalid or has already been used. Request a new one below.';
    }
    if (code === 'auth/operation-not-allowed') {
      return 'Magic link sign-in is not enabled yet. Please sign in with your password or Google.';
    }
    return error.message || 'An error occurred during login';
  };

  // Show spinner while checking auth state or redeeming a magic link
  if (authLoading || completingMagicLink) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
        <Spinner size="lg" className="text-muted-foreground" />
        {completingMagicLink && (
          <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
        )}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background relative overflow-hidden">
      <StagingBanner />
      {/* Background pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('/background.svg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Main content - centered card */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10 overflow-y-auto">
        <div
          className="w-full max-w-[420px] shadow-2xl rounded-xl border border-border sm:border max-sm:shadow-none max-sm:border-0 bg-card/95 text-foreground"
        >
          <div className="p-8">
            {/* Logo with tagline - Centered */}
            <div className="text-center mb-8">
              <img
                src="/logo.png"
                alt="simcheck.ai"
                className="h-16 w-16 object-contain mx-auto mb-4"
              />
              <div className="mb-1">
                <span className="text-2xl tracking-tight">
                  <span className="font-semibold" style={{ color: '#545A5F' }}>simcheck</span>
                  <span className="font-medium" style={{ color: '#75797C' }}>.ai</span>
                </span>
              </div>
              <p className="text-xs tracking-widest uppercase" style={{ color: '#75797C' }}>
                AI-Powered eSIM Assurance
              </p>
              <p className="text-sm text-muted-foreground mt-6">
                Sign in to your account
              </p>
            </div>

            {/* Google Sign-In Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full h-9 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 bg-muted text-foreground hover:bg-muted/80 border-0"
            >
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <span className="flex items-center justify-center whitespace-nowrap">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </span>
              )}
            </button>

            {/* Divider */}
            <div className="my-6 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3 bg-card text-muted-foreground max-sm:bg-background">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6">
                <Alert variant="destructive">
                  {error}
                </Alert>
              </div>
            )}

            {/* Magic link sent confirmation */}
            {mode === 'magic' && magicSentTo ? (
              <div className="space-y-4">
                <Alert>
                  <Mail className="h-4 w-4" />
                  <div>
                    <p className="font-medium text-foreground">Check your inbox</p>
                    <p className="text-muted-foreground mt-1">
                      We sent a sign-in link to{' '}
                      <span className="font-medium text-foreground">{magicSentTo}</span>.
                      Open it on this device to sign in. The link expires after a short while.
                    </p>
                  </div>
                </Alert>
                <button
                  type="button"
                  onClick={() => setMagicSentTo(null)}
                  className="w-full h-9 px-4 py-2 rounded-md text-sm font-medium transition-colors bg-muted text-foreground hover:bg-muted/80 border-0"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              /* Email/Password or Magic Link Form */
              <form onSubmit={mode === 'password' ? handleSubmit : handleMagicSubmit} className="space-y-4">
                {mode === 'magic' && (
                  <p className="text-sm text-muted-foreground">
                    {needsEmailConfirm
                      ? 'Confirm the email address this sign-in link was sent to.'
                      : "We'll email you a one-time link — no password needed."}
                  </p>
                )}

                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground block">
                    Email address<span className="text-destructive ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={loading}
                    className="w-full h-9 px-3 py-1 rounded-md text-sm bg-background text-foreground border border-border transition-colors focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 placeholder:text-muted-foreground"
                  />
                </div>

                {mode === 'password' && (
                  <>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-foreground block">
                        Password<span className="text-destructive ml-1">*</span>
                      </label>
                      <input
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        disabled={loading}
                        className="w-full h-9 px-3 py-1 rounded-md text-sm bg-background text-foreground border border-border transition-colors focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 placeholder:text-muted-foreground"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 rounded"
                        />
                        <label
                          htmlFor="remember-me"
                          className="text-sm text-muted-foreground ml-2"
                        >
                          Remember me
                        </label>
                      </div>

                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium transition-colors text-accent-blue hover:text-accent-blue/80"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-9 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 mt-2 flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 border-0"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : mode === 'password' ? (
                    <span className="flex items-center justify-center whitespace-nowrap">
                      <LogIn className="w-4 h-4 mr-2 flex-shrink-0" />
                      Sign in
                    </span>
                  ) : (
                    <span className="flex items-center justify-center whitespace-nowrap">
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                      {needsEmailConfirm ? 'Finish signing in' : 'Email me a sign-in link'}
                    </span>
                  )}
                </button>
              </form>
            )}

            {/* Mode toggle */}
            {!magicSentTo && (
              <button
                type="button"
                onClick={() => switchMode(mode === 'password' ? 'magic' : 'password')}
                className="w-full mt-4 text-center text-sm font-medium transition-colors text-accent-blue hover:text-accent-blue/80"
              >
                {mode === 'password'
                  ? 'Email me a sign-in link instead'
                  : 'Sign in with a password instead'}
              </button>
            )}

            {/* Sign up link */}
            <p className="mt-8 text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-medium transition-colors text-accent-blue hover:text-accent-blue/80"
              >
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* System Status Footer */}
      <footer className="shrink-0 py-4 px-6 relative z-10">
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-status-success/75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-status-success" />
          </span>
          <span>Global Network Status: Operational</span>
        </div>
      </footer>
    </div>
  );
}
